import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, Printer, Download } from "lucide-react";
import { toast } from "sonner";

export default function SupplierLedger() {
  const [params, setParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [sid, setSid] = useState(params.get("id") || "");
  const [data, setData] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, mode: "cash", reference: "", date: today, notes: "" });

  useEffect(() => {
    api.get("/suppliers").then((r) => {
      setSuppliers(r.data);
      if (!sid && r.data[0]) setSid(r.data[0].id);
    });
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    if (!sid) return;
    try {
      const { data } = await api.get(`/suppliers/${sid}/ledger`);
      setData(data);
      setParams({ id: sid });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [sid, setParams]);

  useEffect(() => { load(); }, [load]);

  const submitPay = async () => {
    if (Number(pay.amount) <= 0) return toast.error("Enter amount");
    try {
      await api.post("/supplier-payments", { supplier_id: sid, ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded");
      setPayOpen(false); setPay({ amount: 0, mode: "cash", reference: "", date: today, notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCSV = () => {
    if (!data?.entries?.length) return;
    const rows = [["Date", "Type", "Ref", "Invoice/Notes", "Debit", "Credit", "Balance"]];
    data.entries.forEach((e) => rows.push([e.date, e.type, e.ref, e.invoice_no || e.notes, e.debit, e.credit, e.balance]));
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ledger-${data.supplier.name}-${today}.csv`;
    a.click();
  };

  return (
    <div className="p-6 sm:p-8" data-testid="supplier-ledger-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">Supplier Ledger</h1>
          <p className="text-sm text-[#7A7873] mt-1">Running balance · purchases vs payments.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sid} onValueChange={setSid}>
            <SelectTrigger className="w-64 h-10 border-[#EBE8E2]" data-testid="ledger-supplier-select">
              <SelectValue placeholder="Choose supplier" />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCSV} className="h-10" data-testid="export-ledger"><Download size={14} className="mr-1.5" />CSV</Button>
          <Button variant="outline" onClick={() => window.print()} className="h-10"><Printer size={14} className="mr-1.5" />Print</Button>
          <Button className="h-10 bg-[#D95D39] hover:bg-[#C24F2C]" onClick={() => setPayOpen(true)} disabled={!sid} data-testid="add-payment-btn">
            <Wallet size={14} className="mr-1.5" /> Record Payment
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 print-area">
            <Card className="p-4 border-[#EBE8E2] bg-white sm:col-span-1">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Supplier</div>
              <div className="font-heading font-bold text-lg text-[#2D2C2A] mt-1">{data.supplier.name}</div>
              <div className="text-xs text-[#7A7873] mt-0.5 font-mono-num">{data.supplier.gstin || "—"}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Total Purchases (Dr)</div>
              <div className="font-heading font-extrabold text-2xl text-[#2D2C2A] mt-1 font-mono-num">{inr(data.totals.total_debit)}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Total Paid (Cr)</div>
              <div className="font-heading font-extrabold text-2xl text-[#6A7F66] mt-1 font-mono-num">{inr(data.totals.total_credit)}</div>
            </Card>
            <Card className="p-4 border-[#EBE8E2] bg-white">
              <div className="text-xs uppercase tracking-wider text-[#7A7873]">Balance Due</div>
              <div className="font-heading font-extrabold text-2xl mt-1 font-mono-num" style={{ color: data.totals.balance > 0 ? "#D95D39" : "#6A7F66" }} data-testid="ledger-balance">
                {inr(data.totals.balance)}
              </div>
            </Card>
          </div>

          <div className="bg-white border border-[#EBE8E2] rounded-lg overflow-x-auto print-area">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-[#F4F2EE] text-[#7A7873] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Ref</th>
                  <th className="text-left px-4 py-2.5">Notes</th>
                  <th className="text-right px-4 py-2.5">Debit</th>
                  <th className="text-right px-4 py-2.5">Credit</th>
                  <th className="text-right px-4 py-2.5">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <tr key={i} className="border-t border-[#EBE8E2]" data-testid={`ledger-row-${i}`}>
                    <td className="px-4 py-2.5 font-mono-num text-xs text-[#7A7873]">{e.date}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                        e.type === "purchase" ? "bg-[#FBE9E2] text-[#D95D39]" :
                        e.type === "payment" ? "bg-[#E5EBE3] text-[#6A7F66]" :
                        "bg-[#F4F2EE] text-[#7A7873]"
                      }`}>{e.type}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono-num text-xs">{e.ref}</td>
                    <td className="px-4 py-2.5 text-[#7A7873] text-xs">{e.invoice_no || e.notes}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{e.debit ? inr(e.debit) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{e.credit ? inr(e.credit) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold" style={{ color: e.balance > 0 ? "#D95D39" : e.balance < 0 ? "#6A7F66" : "#2D2C2A" }}>
                      {inr(e.balance)}
                    </td>
                  </tr>
                ))}
                {data.entries.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-[#7A7873]">No transactions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Record Supplier Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (₹) *</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="pay-amount" /></div>
              <div>
                <Label>Mode</Label>
                <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
                  <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
              <div><Label>Reference</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="Cheque/UTR No" /></div>
            </div>
            <div><Label>Notes</Label><Input value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button className="bg-[#D95D39] hover:bg-[#C24F2C]" onClick={submitPay} data-testid="save-payment">Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
